import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { FlywheelStatus } from "../../vl-api/vlSchemas";
import { useNavigate } from "react-router-dom";
import {
  FLOW_PARAMETER,
  SEED_PARAMETER,
} from "../../helpers/constants/miscellaneous";
import { useClearLabelPropagationSearchParams } from "./useClearLabelPropagationSearchParams";
import { App } from "antd";

interface FlywheelStatusData {
  seed_id?: string;
  flow_id?: string;
  flywheel_status: FlywheelStatus;
}

export const useSeed = (datasetId: string, showSummaryTab: () => void) => {
  const {
    modal: { warning },
  } = App.useApp();
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  const [seedId, setSeedId] = useState<string | null>(null);
  const [flowId, setFlowId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const clearLabelPropagationSearchParams =
    useClearLabelPropagationSearchParams();

  const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

  const fetchSeedAndStatus = useCallback(async () => {
    setLoading(true);

    setHasError(false);
    try {
      const statusUrl = composeApiURL(HOST_URL, `/flywheel/${datasetId}`);
      const res = await axios.get<FlywheelStatusData>(statusUrl);

      setFlowId(res.data.flow_id || null);

      if (res.data.seed_id) {
        setSeedId(res.data.seed_id);
        return res.data.seed_id;
      } else {
        setFlowId(null);
        const seedUrl = composeApiURL(HOST_URL, `/flywheel/${datasetId}/seed`);
        const putRes = await axios.put<{ id: string }>(seedUrl);
        setSeedId(putRes.data.id);
        return putRes.data.id;
      }
    } catch (e) {
      setHasError(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, [datasetId, HOST_URL]);

  useEffect(() => {
    fetchSeedAndStatus();
  }, [fetchSeedAndStatus]);

  useEffect(() => {
    if (!loading) {
      const params = new URLSearchParams(window.location.search);
      if (seedId) {
        if (
          params.has(SEED_PARAMETER) &&
          params.get(SEED_PARAMETER) !== seedId
        ) {
          showSummaryTab();
          clearLabelPropagationSearchParams();
          warning({
            title: "Label Propagation",
            content: "The flow is not longer available",
            autoFocusButton: null,
          });
          return;
        }
        params.set(SEED_PARAMETER, seedId);
        if (flowId) {
          params.set(FLOW_PARAMETER, flowId);
        }
        navigate({ search: params.toString() });
      } else if (!hasError) {
        showSummaryTab();
        clearLabelPropagationSearchParams();
      }
    }
  }, [
    loading,
    seedId,
    flowId,
    navigate,
    clearLabelPropagationSearchParams,
    showSummaryTab,
    warning,
    hasError,
  ]);

  return {
    loading,
  };
};
